import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import AnimatedSection from '../components/AnimatedSection';

const Portfolio = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [filter, setFilter] = useState('all');

  const projects = [
    {
      id: 1,
      title: 'GoldFinery E-Commerce',
      category: 'website',
      type: 'Luxury E-Commerce',
      description: 'A premium jewelry e-commerce platform featuring elegant design, advanced filtering, and seamless checkout experience.',
      image: 'https://mir-s3-cdn-cf.behance.net/project_modules/1400/2f3424115227831.604a38194cd2c.jpg',
      images: [
        'https://images.unsplash.com/photo-1507238691740-187a5b1d37b8',
        'https://images.pexels.com/photos/205316/pexels-photo-205316.png'
      ],
      technologies: ['React', 'Node.js', 'Stripe', 'MongoDB'],
      features: ['Custom Shopping Cart', 'Payment Integration', 'Admin Dashboard', 'Inventory Management'],
      liveUrl: '#',
      results: '300% increase in online sales, 45% improvement in conversion rate'
    },
    {
      id: 2,
      title: 'FitElite Mobile App',
      category: 'app',
      type: 'Fitness & Wellness',
      description: 'A comprehensive fitness app with workout tracking, nutrition planning, and social features for fitness enthusiasts.',
      image: 'https://images.pexels.com/photos/7947956/pexels-photo-7947956.jpeg',
      images: [
        'https://images.pexels.com/photos/6373086/pexels-photo-6373086.jpeg',
        'https://images.unsplash.com/photo-1532295454114-d7bc89024613'
      ],
      technologies: ['React Native', 'Firebase', 'GraphQL', 'Stripe'],
      features: ['Workout Tracking', 'Social Features', 'Nutrition Planning', 'Progress Analytics'],
      liveUrl: '#',
      results: '50k+ downloads in first month, 4.8★ app store rating'
    },
    {
      id: 3,
      title: 'NovaTech Branding',
      category: 'design',
      type: 'Brand Identity',
      description: 'Complete brand identity design including logo, color palette, typography, and marketing materials for a tech startup.',
      image: 'https://www.sierrajluck.com/wp-content/uploads/2023/07/novatech-01-2.png',
      images: [
        'https://images.pexels.com/photos/546819/pexels-photo-546819.jpeg',
        'https://images.unsplash.com/photo-1498050108023-c5249f4df085'
      ],
      technologies: ['Figma', 'Adobe Creative Suite', 'Sketch', 'InVision'],
      features: ['Logo Design', 'Brand Guidelines', 'Marketing Materials', 'Digital Assets'],
      liveUrl: '#',
      results: '200% brand recognition increase, 15% higher customer engagement'
    },
    {
      id: 4,
      title: 'CryptoVault Dashboard',
      category: 'website',
      type: 'Cryptocurrency Platform',
      description: 'A sophisticated trading dashboard with real-time data visualization, portfolio management, and advanced analytics.',
      image: 'https://www.chartlog.com/site/assets/files/1074/trading-dashboard.jpg',
      images: [
        'https://images.unsplash.com/photo-1516321318423-f06f85e504b3',
        'https://images.unsplash.com/photo-1523961131990-5ea7c61b2107'
      ],
      technologies: ['Next.js', 'D3.js', 'WebSocket', 'TailwindCSS'],
      features: ['Real-time Data', 'Advanced Charts', 'Portfolio Tracking', 'Risk Management'],
      liveUrl: '#',
      results: '$2M+ in trading volume, 99.9% uptime achieved'
    },
    {
      id: 5,
      title: 'EcoTrack Mobile',
      category: 'app',
      type: 'Environmental App',
      description: 'An environmental tracking app that helps users monitor their carbon footprint and adopt sustainable practices.',
      image: 'https://miro.medium.com/v2/resize:fit:1200/1*fcjIlenql8Buw3lNLb880g.png',
      images: [
        'https://images.pexels.com/photos/1181335/pexels-photo-1181335.jpeg',
        'https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg'
      ],
      technologies: ['Flutter', 'Firebase', 'ML Kit', 'Google Maps'],
      features: ['Carbon Tracking', 'Eco Challenges', 'Community Features', 'Impact Analytics'],
      liveUrl: '#',
      results: '100k+ active users, 30% reduction in user carbon footprint'
    },
    {
      id: 6,
      title: 'ArtSpace Gallery',
      category: 'design',
      type: 'Art & Culture',
      description: 'Visual identity and exhibition design for a contemporary art gallery, including wayfinding and digital displays.',
      image: 'https://images.adsttc.com/media/images/5a7c/9823/f197/cc44/1a00/0091/large_jpg/bobardi2.jpg?1518114842',
      images: [
        'https://images.unsplash.com/photo-1522071820081-009f0129c71c',
        'https://images.unsplash.com/photo-1542744095-fcf48d80b0fd'
      ],
      technologies: ['Adobe Creative Suite', 'Cinema 4D', 'TouchDesigner', 'Arduino'],
      features: ['Exhibition Design', 'Interactive Displays', 'Wayfinding System', 'Digital Catalog'],
      liveUrl: '#',
      results: '40% increase in visitor engagement, award-winning design'
    }
  ];

  const categories = [
    { id: 'all', name: 'All Projects' },
    { id: 'website', name: 'Websites' },
    { id: 'app', name: 'Mobile Apps' },
    { id: 'design', name: 'Design' }
  ];

  const filteredProjects = filter === 'all' 
    ? projects 
    : projects.filter(project => project.category === filter);

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-hero-pattern opacity-30" />
        <div className="section-container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl lg:text-7xl font-bold text-cleantech-white mb-6">
              Our <span className="text-gradient">Portfolio</span>
            </h1>
            <p className="text-xl lg:text-2xl text-white/80 max-w-4xl mx-auto">
              Explore our collection of successful projects that showcase our expertise in creating 
              exceptional digital experiences across various industries.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="py-8">
        <div className="section-container">
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setFilter(category.id)}
                className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                  filter === category.id
                    ? 'bg-cleantech-gold text-cleantech-charcoal shadow-gold'
                    : 'bg-white/5 text-cleantech-white hover:bg-cleantech-gold/20 hover:text-cleantech-gold'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="pb-20">
        <div className="section-container">
          <motion.div 
            layout
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            <AnimatePresence>
              {filteredProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -10 }}
                  className="glass-card overflow-hidden hover:shadow-gold transition-all duration-500 cursor-pointer group"
                  onClick={() => setSelectedProject(project)}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-cleantech-charcoal to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="absolute top-4 right-4 w-10 h-10 bg-cleantech-gold rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                      <ArrowTopRightOnSquareIcon className="w-5 h-5 text-cleantech-charcoal" />
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="text-cleantech-gold text-sm font-medium mb-2">{project.type}</div>
                    <h3 className="text-xl font-bold text-cleantech-white mb-3 group-hover:text-cleantech-gold transition-colors duration-300">
                      {project.title}
                    </h3>
                    <p className="text-white/80 line-clamp-3">{project.description}</p>
                    
                    <div className="mt-4 flex flex-wrap gap-2">
                      {project.technologies.slice(0, 3).map((tech, techIndex) => (
                        <span
                          key={techIndex}
                          className="px-2 py-1 bg-cleantech-gold/20 text-cleantech-gold rounded text-xs"
                        >
                          {tech}
                        </span>
                      ))}
                      {project.technologies.length > 3 && (
                        <span className="px-2 py-1 bg-white/10 text-white/60 rounded text-xs">
                          +{project.technologies.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* Project Modal */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setSelectedProject(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass-card max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative">
                <button
                  onClick={() => setSelectedProject(null)}
                  className="absolute top-4 right-4 z-10 w-10 h-10 bg-cleantech-charcoal/80 rounded-lg flex items-center justify-center hover:bg-cleantech-gold hover:text-cleantech-charcoal transition-all duration-300"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
                
                <img
                  src={selectedProject.image}
                  alt={selectedProject.title}
                  className="w-full h-64 lg:h-80 object-cover"
                />
              </div>
              
              <div className="p-8">
                <div className="flex flex-wrap items-center justify-between mb-6">
                  <div>
                    <div className="text-cleantech-gold text-sm font-medium mb-2">{selectedProject.type}</div>
                    <h2 className="text-3xl lg:text-4xl font-bold text-cleantech-white">{selectedProject.title}</h2>
                  </div>
                  <a
                    href={selectedProject.liveUrl}
                    className="btn-primary mt-4 lg:mt-0"
                  >
                    View Live
                  </a>
                </div>
                
                <p className="text-lg text-white/80 mb-8">{selectedProject.description}</p>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                  <div>
                    <h3 className="text-xl font-bold text-cleantech-white mb-4">Key Features</h3>
                    <ul className="space-y-2">
                      {selectedProject.features.map((feature, index) => (
                        <li key={index} className="flex items-center text-white/80">
                          <div className="w-2 h-2 bg-cleantech-gold rounded-full mr-3" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-bold text-cleantech-white mb-4">Technologies Used</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedProject.technologies.map((tech, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-cleantech-gold/20 text-cleantech-gold rounded-lg"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="bg-cleantech-gold/10 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-cleantech-white mb-3">Project Results</h3>
                  <p className="text-cleantech-gold font-medium">{selectedProject.results}</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Portfolio;